import React, { useEffect, useRef } from 'react';

/**
 * Lightweight Leaflet + OpenStreetMap wrapper.
 *
 * Usage:
 *   <ShipmentMap points={[
 *     { id, lat, lng, title, subtitle }, ...
 *   ]} height={400} />
 *
 * Leaflet is loaded globally via CDN in public/index.html.
 */
const ShipmentMap = ({ points = [], height = 400, testId = 'shipment-map' }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.L) return;
    if (!containerRef.current) return;
    if (!mapRef.current) {
      const L = window.L;
      mapRef.current = L.map(containerRef.current, {
        scrollWheelZoom: false,
        zoomControl: true,
      }).setView([39.5, -98.35], 3); // continental US default
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap',
      }).addTo(mapRef.current);
      layerRef.current = L.layerGroup().addTo(mapRef.current);
    }
    // Redraw markers
    const L = window.L;
    layerRef.current.clearLayers();
    const valid = (points || []).filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
    valid.forEach((p) => {
      const m = L.marker([p.lat, p.lng]).addTo(layerRef.current);
      const html = `<div style="font-family:sans-serif;font-size:12px;">
        <div style="font-weight:600;">${escapeHtml(p.title || '')}</div>
        <div style="color:#475569;">${escapeHtml(p.subtitle || '')}</div>
      </div>`;
      m.bindPopup(html);
    });
    if (valid.length) {
      const bounds = L.latLngBounds(valid.map((p) => [p.lat, p.lng]));
      mapRef.current.fitBounds(bounds.pad(0.35), { maxZoom: 14 });
    }
    // ensure the map recalculates its size (in case it was hidden)
    setTimeout(() => mapRef.current?.invalidateSize(), 50);
  }, [points]);

  return (
    <div className="relative w-full rounded overflow-hidden border" style={{ height }} data-testid={testId}>
      <div ref={containerRef} style={{ height: '100%', width: '100%' }} />
      {(!points || points.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng)).length === 0) && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/70 text-slate-500 text-sm">
          No geotagged deliveries in this view.
        </div>
      )}
    </div>
  );
};

const escapeHtml = (s) => String(s || '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

export default ShipmentMap;
